<template>
  <!-- Search Trigger Button -->
  <div class="support-search-trigger-outer">
    <button 
    class="support-search-trigger"
    @click="openModal"
    aria-label="Search documentation"
  >
    <svg 
      class="support-search-trigger__icon" 
      xmlns="http://www.w3.org/2000/svg" 
      width="16" 
      height="16" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      stroke-width="2" 
      stroke-linecap="round" 
      stroke-linejoin="round"
    >
      <circle cx="11" cy="11" r="8"></circle>
      <path d="m21 21-4.35-4.35"></path>
    </svg>
    <span class="support-search-trigger__text">Search...</span>
    <kbd class="support-search-trigger__kbd">⌘K</kbd>
  </button>
  </div>

  <!-- Modal Overlay -->
  <teleport to="body">
    <transition name="search-modal-fade">
      <div 
        v-if="isOpen"
        class="support-search-modal"
        @click="handleBackdropClick"
      >
        <!-- Modal Content -->
        <div 
          class="support-search-modal__content"
          ref="modalContent"
          @click.stop
        >
          <!-- Search Input -->
          <div class="support-search-modal__input-wrapper">
            <svg 
              class="support-search-modal__search-icon" 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              placeholder="Search documentation..."
              class="support-search-modal__input"
              @input="handleInput"
              @keydown="handleKeydown"
            />

            <div v-if="loading" class="support-search-modal__spinner">
              <svg class="support-search-modal__spinner-icon" viewBox="0 0 24 24">
                <circle 
                  class="support-search-modal__spinner-circle" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  stroke-width="4" 
                  fill="none"
                />
              </svg>
            </div>
          </div>

          <!-- Results Area -->
          <div class="support-search-modal__results">
            <!-- Initial State -->
            <div v-if="!searchQuery && results.length === 0" class="support-search-modal__empty">
              <svg 
                class="support-search-modal__empty-icon"
                xmlns="http://www.w3.org/2000/svg" 
                width="48" 
                height="48" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="1.5" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <p class="support-search-modal__empty-text">Start typing to search documentation</p>
            </div>

            <!-- Loading State -->
            <div v-else-if="loading && results.length === 0" class="support-search-modal__empty">
              <p class="support-search-modal__empty-text">Searching...</p>
            </div>

            <!-- No Results -->
            <div v-else-if="!loading && searchQuery.length > 0 && results.length === 0" class="support-search-modal__empty">
              <p class="support-search-modal__empty-text">No results found for "{{ searchQuery }}"</p>
            </div>

            <!-- Results List -->
            <ul v-else-if="results.length > 0" class="support-search-modal__results-list">
              <li
                v-for="(result, index) in results"
                :key="result.id"
                :class="[
                  'support-search-modal__result-item',
                  { 'support-search-modal__result-item--active': selectedIndex === index }
                ]"
                @click="navigateToArticle(result)"
                @mouseenter="selectedIndex = index"
              >
                <div class="support-search-modal__result-title">{{ result.name }}</div>
                <div v-if="result.preview" class="support-search-modal__result-preview">
                  {{ truncatePreview(result.preview) }}
                </div>
                <div v-if="result.collectionName" class="support-search-modal__result-breadcrumb">
                  {{ result.collectionName }}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { debounce } from 'lodash';
import helpScoutService from '@/services/helpscout';
import helpScoutState from '@/services/helpscoutState';

export default {
  name: 'SupportSearch',
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      results: [],
      loading: false,
      selectedIndex: -1,
    };
  },
  created() {
    // Create debounced search function
    this.debouncedSearch = debounce(this.performSearch, 300);
  },
  mounted() {
    // Add keyboard shortcut listener (Cmd+K or Ctrl+K)
    document.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleGlobalKeydown);
    // Cancel any pending debounced calls
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel();
    }
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        // Modal opened - focus input and prevent body scroll
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
          document.body.style.overflow = 'hidden';
        });
      } else {
        // Modal closed - restore body scroll
        document.body.style.overflow = '';
      }
    },
  },
  methods: {
    openModal() {
      this.isOpen = true;
    },
    closeModal() {
      this.isOpen = false;
      this.searchQuery = '';
      this.results = [];
      this.selectedIndex = -1;
    },
    handleGlobalKeydown(event) {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (this.isOpen) {
          this.closeModal();
        } else {
          this.openModal();
        }
      }
    },
    handleBackdropClick() {
      this.closeModal();
    },
    handleInput() {
      // Reset selection when typing
      this.selectedIndex = -1;
      
      // Clear results if query is empty
      if (this.searchQuery.trim().length === 0) {
        this.results = [];
        return;
      }

      // Set loading and perform search
      this.loading = true;

      // Debounced search
      this.debouncedSearch();
    },
    async performSearch() {
      const query = this.searchQuery.trim();
      
      if (query.length === 0) {
        this.loading = false;
        return;
      }

      try {
        const response = await helpScoutService.searchArticles(query);
        
        if (Array.isArray(response)) {
          // Enrich results with collection names
          this.results = await this.enrichResultsWithCollections(response);
        } else {
          this.results = [];
        }
      } catch (error) {
        console.error('Search error:', error);
        this.results = [];
      } finally {
        this.loading = false;
      }
    },
    async enrichResultsWithCollections(articles) {
      // Get collections to map IDs to names/slugs
      const collections = await helpScoutState.getCollections(helpScoutService);
      
      return articles.map(article => {
        const collection = collections.find(c => c.id === article.KbCollectionId);
        return {
          ...article,
          collectionName: collection ? collection.name : null,
          collectionSlug: collection ? collection.slug : null,
        };
      });
    },
    navigateToArticle(article) {
      // Build route using collection slug (or ID as fallback) and article slug (or ID)
      const collectionIdentifier = article.collectionSlug || article.collectionId;
      const articleIdentifier = article.slug || article.id;
      
      if (collectionIdentifier && articleIdentifier) {
        this.$router.push(`/support/${collectionIdentifier}/${articleIdentifier}`);
        
        // Close modal
        this.closeModal();
      }
    },
    handleKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (this.results.length > 0) {
            this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1);
            this.scrollToSelected();
          }
          break;
        
        case 'ArrowUp':
          event.preventDefault();
          if (this.results.length > 0) {
            this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
            this.scrollToSelected();
          }
          break;
        
        case 'Enter':
          event.preventDefault();
          if (this.selectedIndex >= 0 && this.selectedIndex < this.results.length) {
            this.navigateToArticle(this.results[this.selectedIndex]);
          }
          break;
        
        case 'Escape':
          event.preventDefault();
          this.closeModal();
          break;
      }
    },
    scrollToSelected() {
      // Scroll selected item into view
      this.$nextTick(() => {
        const selectedItem = this.$refs.modalContent?.querySelector('.support-search-modal__result-item--active');
        if (selectedItem) {
          selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },
    clearSearch() {
      this.searchQuery = '';
      this.results = [];
      this.selectedIndex = -1;
      this.$refs.searchInput?.focus();
    },
    truncatePreview(text, maxLength = 120) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + '...';
    },
  },
};
</script>

